/* eslint-disable @next/next/no-img-element */
'use client';

import Card from '@/components/Card/Card';
import ImagesCarousel from '@/components/ImagesCarousel/ImagesCarousel';
import Portfolio from '@/components/Portfolio/Portfolio';
import { PortfolioTagLinks } from '@/components/Portfolio/PortfolioTags';
import { Locale } from '@/i18n/routing';
import Logo from '@/resources/assets/Logo';
import { getPortfolioItemByPath } from '@/resources/messages/portfolio/portfolio';
import { mdiOpenInNew } from '@mdi/js';
import Icon from '@mdi/react';
import { useReducedMotion, motion } from 'motion/react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { notFound, useParams } from 'next/navigation';
import { useMemo } from 'react';
import QRCode from 'react-qr-code';
import { twMerge } from 'tailwind-merge';

export default function PortfolioItemPage() {
	const reduceMotion = useReducedMotion();

	const translations = useTranslations();

	const { locale, portfolioItemPath } = useParams<{
		locale: Locale;
		portfolioItemPath: string;
	}>();

	const portfolioItem = useMemo(
		() => getPortfolioItemByPath(locale, portfolioItemPath),
		[locale, portfolioItemPath],
	);

	if (!portfolioItem) {
		notFound();
	}

	return (
		<motion.div
			className="min-h-screen overflow-y-auto overflow-x-hidden pt-8"
			initial={{
				opacity: 0,
				y: reduceMotion ? 0 : '2rem',
			}}
			animate={{
				opacity: 1,
				y: 0,
			}}
		>
			<nav className="relative mx-auto w-fit px-4">
				<div className="absolute inset-0 scale-x-150 scale-y-125 rounded-full bg-white blur-xl dark:bg-black/20" />
				<Link
					href="/"
					aria-label="Home"
					title="Home"
					className="relative z-10 mx-auto block w-fit rounded-3xl p-2"
				>
					<motion.div
						className="relative z-10 w-fit"
						whileHover={{
							scale: reduceMotion ? 1 : 1.05,
						}}
						whileTap={{
							scale: reduceMotion ? 1 : 0.95,
						}}
						tabIndex={-1}
					>
						<Logo className="max-h-32 w-full max-w-xs rotate-6 drop-shadow-sm" />
					</motion.div>
				</Link>
			</nav>

			<main className="relative">
				{'images' in portfolioItem &&
					!!portfolioItem.images &&
					(portfolioItem.images?.length ?? 0) > 0 && (
						<ImagesCarousel images={portfolioItem.images} />
					)}
				<div className="mx-auto grid max-w-6xl gap-4 px-2 py-4 sm:px-8 md:px-16 lg:px-24">
					<hgroup className="relative z-10 px-1 text-center sm:text-start">
						<h1 className="text-3xl font-black sm:text-4xl md:text-5xl">
							{portfolioItem.title}
						</h1>
						<p className="py-2 text-xl">
							{portfolioItem.type === 'static' &&
								!!portfolioItem.subtitle &&
								portfolioItem.subtitle}
						</p>
						<p className="inline-block -translate-x-1 rounded-full bg-rose-700 px-3 text-lg text-white">
							{portfolioItem.date[0]}
						</p>
					</hgroup>
					<Card className="flex grid-cols-12 flex-col items-stretch gap-4 lg:grid lg:items-start">
						{!!portfolioItem.description && (
							<div
								className={twMerge(
									'grid gap-3 lg:text-balance px-1 text-lg lg:text-xl lg:leading-relaxed font-medium lg:py-2 lg:ps-2',
									portfolioItem.links.length > 0 ||
										('icon' in portfolioItem &&
											!!portfolioItem.icon)
										? 'col-span-8'
										: 'col-span-12',
								)}
							>
								{portfolioItem.description
									.split('\n')
									.map((part, index) => (
										<p key={index}>{part}</p>
									))}
							</div>
						)}
						{!!portfolioItem.links &&
							portfolioItem.links.length > 0 && (
								<aside className="bg-cornflower-500/5 col-span-4 grid size-full place-content-start justify-stretch gap-4 rounded-lg border-2 border-black/10 dark:border-white/10 p-4">
									{'icon' in portfolioItem &&
									!!portfolioItem.icon ? (
										<>
											<img
												src={portfolioItem.icon.src}
												alt={portfolioItem.icon.alt}
												className={twMerge(
													'mx-auto h-10 w-10 shrink-0 rounded-full lg:h-14 lg:w-14',
													'border-2 border-black/10 dark:border-white/10 object-cover',
													'translate-x-1 translate-y-1',
												)}
											/>
											<h2 className="sr-only">
												{translations(
													'Portfolio.links',
												)}
											</h2>
										</>
									) : (
										<h2 className="text-cornflower-700 dark:text-cornflower-200 text-center text-lg">
											{translations('Portfolio.links')}
										</h2>
									)}

									<ul className="grid grid-cols-1 items-start gap-2 sm:grid-cols-2 md:gap-3 lg:grid-cols-1">
										{portfolioItem.links.map(
											(link, index) => (
												<motion.li
													key={index}
													tabIndex={-1}
													whileHover={{
														scale: reduceMotion
															? 1
															: 1.05,
													}}
													whileTap={{
														scale: reduceMotion
															? 1
															: 0.95,
													}}
												>
													<Link
														href={link.url}
														className={twMerge(
															'block rounded-3xl text-lg border-2 border-black/10 dark:border-white/10',
															index === 0
																? 'bg-cornflower-500 text-white'
																: 'bg-white text-cornflower-500',
														)}
														target="_blank"
														rel="noopener noreferrer"
														aria-label={`${
															link.title
														} (${translations(
															'Common.external_link',
														)})`}
													>
														{link.qr?.isEnabled && (
															<QRCode
																value={link.url}
																className="mx-auto size-44 px-4 pt-4"
																bgColor="transparent"
																fgColor="currentColor"
																aria-label="QR-code"
															/>
														)}

														<div
															className={twMerge(
																'flex justify-between gap-1 ps-5 pe-3 py-2 items-center',
															)}
														>
															{link.title}
															<Icon
																path={
																	mdiOpenInNew
																}
																size={1}
																className="shrink-0"
															/>
														</div>
													</Link>
												</motion.li>
											),
										)}
									</ul>
								</aside>
							)}
					</Card>

					<PortfolioTagLinks tags={portfolioItem.tags} />

					<h2 className="sr-only">
						{translations('Portfolio.related')}
					</h2>
					<Portfolio
						tags={portfolioItem.tags}
						maxItems={6}
						excludeItemsWithPaths={[portfolioItem.path]}
					/>
				</div>
			</main>
		</motion.div>
	);
}
